import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// Components
import Modal, { ModalBody } from "Components/Modal";
import { ListItemIcon, ListItemText, Divider, ListItem, Typography, Grid, Popper, ClickAwayListener, Paper } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import { ToggleSwitch, InputSearch, Button, Checkbox, Avatar } from "Components";
import { AvatarChat } from '../../../../.globals/helpers';
import { NotificationManager } from 'Components/Notifications';
import TruncatedText from './TruncatedText';

import { reset, close, createChatChannel } from "../redux/contactsSlice";
import ChatIcon from "@mui/icons-material/NavigateNext";
import IconButton from '@mui/material/IconButton';
import CheckboxFilledSvg from "../../../../../../components/SvgIcon/CheckboxFilledSvg";
import EmptyPlaceholder from './../../../../components/EmptyPlaceholder';

const TOOLTIP_POPPER_PROPS = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [12, 0],
      },
    },
  ],
};

const TOOLTIP_COMPONENTS_PROPS = {
  tooltip: {
    sx: {
      maxWidth: '470px',
    }
  }
};

// Extract UserListItem outside component and memoize
const UserListItem = React.memo(({ row, authId, chatUsers, messages, createChatChannel }) => {
  return (
    <ListItem
      className="chatItem"
      onClick={(e) => {
        e.preventDefault();
        const name = chatUsers
          .filter(x => x.authId === authId || x.authId === row.authId)
          .map(x => x.label)
          .join('|');
        createChatChannel({ name, users: [authId, row.authId] });
      }}
      secondaryAction={
        <IconButton
          aria-label="comment"
        >
          <ChatIcon sx={{ color: '#212121' }} />
        </IconButton>
      }
    >
      <ListItemIcon className="chat-icon">
        <AvatarChat user={row} showStatusIcon={true} />
      </ListItemIcon>
      <ListItemText sx={{ minWidth: 0, overflow: 'hidden' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ fontSize: 14, color: "#212121", overflow: 'hidden' }} style={{ lineHeight: "100%" }} component="span">
            <TruncatedText
              text={row?.label}
              tooltipPlacement="bottom"
              tooltipPopperProps={TOOLTIP_POPPER_PROPS}
              tooltipComponentsProps={TOOLTIP_COMPONENTS_PROPS}
            />
          </Typography>
          {row?.entityName && <span
            style={{
              backgroundColor: "#2E7D32",
              color: "#FFFFFF",
              fontSize: 11,
              borderRadius: 4,
              padding: "2.3px 7.5px",
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >{messages[row.entityName]}</span>}
        </span>
      </ListItemText>
    </ListItem>
  );
});

const TeamMemberSelector = ({ onClose }) => {
  const [driverConnected, setDriverConnected] = useState(false);
  // Filter menu states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterBtnRef = React.useRef(null);
  const [filterOnlineOnly, setFilterOnlineOnly] = useState(false);
  const [filterShowUsers, setFilterShowUsers] = useState(true);
  const [filterShowDrivers, setFilterShowDrivers] = useState(true);
  // Filter menu handlers
  const handleFilterMenuOpen = useCallback((event) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterMenuOpen(true);
  }, []);
  const handleFilterMenuClose = useCallback(() => {
    setFilterMenuOpen(false);
    setFilterAnchorEl(null);
  }, []);
  const { messages } = useIntl();

  //selectors
  const dispatch = useDispatch();

  const { openModal, result, error } = useSelector((state) => state.chatListContactsRedux);
  const { authId } = useSelector((state) => state.securityRedux);
  const chatUsers = useSelector((state) => state.catalogsRedux.chatUsers);
  const loadingChatUsers = useSelector((state) => state.catalogsRedux.loadingChatUsers);

  const [searchText, setSearchText] = useState("");

  // Memoize createChatChannel handler for UserListItem
  const handleCreateChat = useCallback((payload) => {
    dispatch(createChatChannel(payload));
  }, [dispatch]);

  useEffect(() => {
    reset({ error: null, result: null })
    if (error) {
      NotificationManager.error(
        messages['errorMessage'],
        messages['error'],
        3000,
        null,
        null,
        'warning',
        null
      );
    }

    if (result) {
      handleClose(result)
    }

  }, [result, error])


  const handleClose = useCallback((item) => {
    dispatch(reset());
    onClose(item)
    dispatch(close())
  }, [dispatch, onClose]);

  const filterBtnStyle = {
    border: '1px solid #2CC6FF',
    color: '#2CC6FF',
    background: 'white',
    marginRight: 8,
    display: 'flex',
    alignItems: 'center',
    borderRadius: 8,
    height: 40,
    justifyContent: 'center',
    padding: '0 20px 0 16px',
  }

  const filterMenuStyle = {
    border: '1px solid #E2E4E6',
    borderRadius: 8,
    minWidth: 200,
    padding: '4px 0',
    marginTop: 4,
  }

  const handleSearchChange = useCallback((e) => {
    setSearchText(e || '');
  }, []);

  // Memoize filtered list to avoid recomputation on every render
  const filteredUsers = useMemo(() => {
    return chatUsers?.filter(x => {
      // Exclude current user
      if (x.authId === authId) return false;

      const { entityName } = x;
      if (searchText) {
        const trimmedSearchText = searchText.trim();
        // If search is only whitespace, show all results
        if (!trimmedSearchText) {
          return true;
        }
        const labelLower = x.label?.toLowerCase();
        const searchLower = trimmedSearchText.toLowerCase();
        // Apply search filter
        if (!labelLower?.includes(searchLower)) {
          return false;
        }
      }

      // Apply entity type filter (Show drivers / Show users)
      // If both filters are OFF: Display nothing
      if (!filterShowUsers && !filterShowDrivers) {
        return false;
      }

      // If only one filter is ON: Show only that entity type
      // Treat MASTERUSER (Account Owner) as SUBUSER (User) for filtering
      if (!filterShowUsers && filterShowDrivers && entityName !== 'DRIVER') {
        return false;
      }
      if (filterShowUsers && !filterShowDrivers && entityName !== 'SUBUSER' && entityName !== 'MASTERUSER') {
        return false;
      }
      // If both filters are ON: Show both drivers and users (already handled above)

      // Apply online status filter
      if (filterOnlineOnly) {
        const isOnline = x.lastConnected && moment().diff(moment(x.lastConnected), 'minute') < 3;
        if (!isOnline) return false;
      }

      return true;
    }) || [];
  }, [chatUsers, authId, searchText, filterShowUsers, filterShowDrivers, filterOnlineOnly]);

  return (
    <Modal
      title={messages['chat_newChat']}
      open={openModal}
      loading={loadingChatUsers}
      size={`small`}
      handleClose={() => {
        handleClose()
      }}
      position="right"
      className="chat-list-contacts-modal"
    >
      <ModalBody style={{ overflow: "hidden", padding: 0, height: 'calc(100% - 160px)', marginBottom: 0, marginTop: "64px" }}>
        {/* <div style={{ padding: 5, marginBottom: 5 }}></div> */}
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          wrap="nowrap"
          style={{ padding: 12, width: "100%" }}
        >
          {/* Filter Button */}
          <Button
            ref={filterBtnRef}
            type="button"
            style={filterBtnStyle}
            onClick={handleFilterMenuOpen}
            aria-label="Filter"
          >
            <FilterListIcon sx={{ color: "#2CC6FF" }} />
            <Typography
              style={{ color: '#2CC6FF', fontWeight: 600, fontSize: 14 }}
              variant="body2"
              color="textSecondary"
            >{messages['user_filter']}</Typography>
          </Button>
          {/* Filter Menu Dropdown using Popper */}
          <Popper open={filterMenuOpen} anchorEl={filterAnchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={handleFilterMenuClose}>
              <Paper className="filter-contact-menu" elevation={3} style={filterMenuStyle}>
                <div>
                  <Checkbox
                    id="filter-online-only"
                    value={filterOnlineOnly}
                    onChange={(checked) => setFilterOnlineOnly(checked)}
                    label={messages['online_only']}
                    size="small"
                    checkedIcon={<CheckboxFilledSvg width={24} height={24} />}
                  />
                </div>
                <div>
                  <Checkbox
                    id="filter-show-users"
                    value={filterShowUsers}
                    onChange={(checked) => setFilterShowUsers(checked)}
                    label={messages['show_users']}
                    size="small"
                    checkedIcon={<CheckboxFilledSvg width={24} height={24} />}
                  />
                </div>
                <div>
                  <Checkbox
                    id="filter-show-drivers"
                    value={filterShowDrivers}
                    onChange={(checked) => setFilterShowDrivers(checked)}
                    label={messages['show_drivers']}
                    size="small"
                    checkedIcon={<CheckboxFilledSvg width={24} height={24} />}
                  />
                </div>
              </Paper>
            </ClickAwayListener>
          </Popper>
          {/* Search Input */}
          <InputSearch
            id="search-chat"
            searchText={searchText}
            onSearch={handleSearchChange}
            placeholder={messages["search_user"]}
            className="w-100"
          />
        </Grid>
        <Divider/>
        <div className="scrollbar-style" style={{ overflowY: 'auto', height: 'calc(100% - 65px)' }}>
          {filteredUsers.length === 0 ? (
            <EmptyPlaceholder />
          ) : (
            filteredUsers.map((row, index) => (
              <div key={`messageI${index}`}>
                <UserListItem
                  row={row}
                  authId={authId}
                  chatUsers={chatUsers}
                  messages={messages}
                  dispatch={dispatch}
                  createChatChannel={handleCreateChat}
                />
                <Divider />
              </div>
            ))
          )}
        </div>
      </ModalBody>
    </Modal >
  );
};

export default TeamMemberSelector;